import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, SelectPicker, Checkbox, Divider } from 'rsuite';
import type { SortType } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import TablePagination from '@/components/TablePagination';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { SubscriptionCode, SubscriptionCodePaginationFilterRequest } from '@/models/subscription-code.model';
import { getPagedSubscriptionCodes } from '@/services/subscription-code.service';
import { jsDateToZone } from '@/utils/date-time';
import PageContent from '@/components/PageContent';
import { getSubscriptionPlansLookup } from '@/services/subscription-plan.service';
import { SubscriptionDurationList, SubscriptionTierList } from '@/models/subscription-plan.model';
import { utc_iana_time_zone_id } from '@/constants/time';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface SubscriptionCodeListPageProps {
  subscriptionPlanId?: number;
} 

const SubscriptionCodeListPage : React.FC<SubscriptionCodeListPageProps> = ({subscriptionPlanId}) => {

  const [filterReq, setFilterReq] = useState<SubscriptionCodePaginationFilterRequest>(new SubscriptionCodePaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<SubscriptionCode[]>([]);
  const [loading, setLoading] = useState(true);

  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: SubscriptionCodePaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        subscriptionPlanId: subscriptionPlanId ?? filterReq.subscriptionPlanId
      };
      
      const result: PagedResponseModel<SubscriptionCode> = await getPagedSubscriptionCodes(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Subscription Codes:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/subscription/codes/new`, { state: { subscriptionPlanId: subscriptionPlanId } })}>
          Add Subscription Code
        </Button>
        <Stack spacing={6}>
          {!subscriptionPlanId && <FetchingSelect
            label="Subscription Plan"
            fetchData={(text, page) => getSubscriptionPlansLookup(text, page)}
            value={filterReq.subscriptionPlanId}
            onChange={dataKey => setFilterReq(pre => ({...pre, subscriptionPlanId: dataKey}))}
          />}               
          {!subscriptionPlanId && <SelectPicker
            label="Tier"
            data={SubscriptionTierList}
            searchable={false}
            value={filterReq.tier ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, tier: dataKey ?? undefined }))} 
          />}          
          {!subscriptionPlanId && <SelectPicker
            label="Duration"
            data={SubscriptionDurationList}
            searchable={false}
            value={filterReq.duration ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, duration: dataKey ?? undefined }))} 
          />}       
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({...pre, keyword: dataKey}))}
          />
          <Checkbox onChange={(value, checked) => setFilterReq(pre => ({...pre, onlyActive: checked == true ? true : undefined}))} >Only Active</Checkbox>
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={2} align="left" sortable>
          <HeaderCell>Code</HeaderCell>
          <Cell dataKey="code"/>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Max Uses</HeaderCell>
          <Cell dataKey="maxUses"/>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Uses Count</HeaderCell>
          <Cell dataKey="usesCount"/>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Start Date</HeaderCell>
          <Cell dataKey="startDate">
            {(rowData: SubscriptionCode) => jsDateToZone(rowData.startDate, utc_iana_time_zone_id)}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>End Date</HeaderCell>
          <Cell dataKey="endDate">
            {(rowData: SubscriptionCode) => rowData.endDate ? jsDateToZone(rowData.endDate, utc_iana_time_zone_id) : '-'}
          </Cell>
        </Column>
        <Column flexGrow={1} align="first" sortable>
          <HeaderCell>Tier</HeaderCell>
          <Cell dataKey="tier">
            {(rowData: SubscriptionCode) => SubscriptionTierList.findLast(i => i.value == rowData.subscriptionPlan?.tier)?.label ?? 'unknown'}
          </Cell>
        </Column>
        <Column flexGrow={1} align="first" sortable>
          <HeaderCell>Duration</HeaderCell>
          <Cell dataKey="duration">
            {(rowData: SubscriptionCode) => SubscriptionDurationList.findLast(i => i.value == rowData.subscriptionPlan?.duration)?.label ?? 'unknown'}
          </Cell>
        </Column>        
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Is Active</HeaderCell>
          <Cell style={{ padding: 5 }}>
          {(rowData: SubscriptionCode) => (   
            <Checkbox checked={rowData.isActive} readOnly></Checkbox>
          )}
          </Cell>
        </Column>
        
        <Column flexGrow={0.5} fixed="right" >
        <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {rowData => (         
              <IconButton onClick={() => nav(`/subscription/codes/${rowData.id}`, { state: { subscriptionPlanId: subscriptionPlanId } })} icon={<EditIcon />} circle size="sm" />  
            )}
          </Cell>
        </Column>
        
      </Table>
      <Divider/>
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default SubscriptionCodeListPage;
